import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryKeys } from 'src/utils/queryKeys';

import { fetchScheduleDaypartOverrides } from '../scheduleDaypartOverrides/useGetScheduleDaypartOverrides';
import { fetchScheduleDayparts } from './useGetScheduleDaypart';
import { mapDaypartsWithOverrides } from './useGetScheduleDaypartsWithOverrides.helper';

export const useGetScheduleDaypartsWithOverrides = (
  locationGroupId: number,
  locationCode?: string,
) => {
  const queries = useMemo(() => {
    const lgDaypartsQuery = [`location_group_id=${locationGroupId}`];
    const locDaypartsQuery = [`location_code=${locationCode}`];

    return [
      {
        queryKey: queryKeys.scheduleDayparts.all(lgDaypartsQuery),
        queryFn: () => fetchScheduleDayparts(lgDaypartsQuery),
        enabled: Boolean(locationGroupId),
      },
      {
        queryKey: queryKeys.scheduleDaypartOverrides.all(locDaypartsQuery),
        queryFn: () => fetchScheduleDaypartOverrides(locDaypartsQuery),
        enabled: Boolean(locationCode),
      },
    ];
  }, [locationGroupId, locationCode]);

  const results = useQueries({ queries });

  const enabledResults = useMemo(
    () => results.filter((_, index) => queries[index].enabled),
    [results, queries],
  );

  const combinedData = useMemo(() => {
    const isLoading = enabledResults.some((result) => result.isLoading);
    const isError = enabledResults.some((result) => result.isError);

    const daypartsData = (enabledResults[0]?.data as Array<any>) || [];

    const overridesData = (enabledResults[1]?.data as Array<any>) || [];

    const structuredData = mapDaypartsWithOverrides(
      daypartsData,
      overridesData,
    );

    return {
      data: structuredData,
      isLoading,
      isError,
      error: enabledResults.find((result) => result.error)?.error,
    };
  }, [enabledResults]);

  return combinedData;
};
